import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useInvoiceFormData } from './hooks/useInvoiceFormData';
import { useInvoiceCalculations } from './hooks/useInvoiceCalculations';
import { useInvoiceUpdate } from './hooks/useInvoiceUpdate';
import InvoiceProgressSummary from './components/InvoiceProgressSummary';
import InvoiceFormWrapper from './components/InvoiceFormWrapper';
import InvoiceFormContent from './components/InvoiceFormContent';
import InvoiceFormFields from './components/InvoiceFormFields';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AlertTriangle } from 'lucide-react';
import { checkInvoiceEditability } from './utils/invoiceEditability';
import { toast } from 'sonner';
import StandaloneInvoiceForm from './standalone/StandaloneInvoiceForm';

const invoiceFormSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  due_date: z.date().optional(),
  payment_terms: z.string().optional(),
  special_instructions: z.string().optional(),
  invoice_type: z.enum(['full', 'partial', 'custom_amount', 'percentage']),
  percentage_of_estimate: z.number().min(1).max(100).optional(),
  custom_amount: z.number().min(0).optional(),
  selected_line_items: z.array(z.string()).optional(),
});

type InvoiceFormValues = z.infer<typeof invoiceFormSchema>;

interface EnhancedEditInvoiceFormProps {
  invoiceId: string;
}

interface LineItemPercentage {
  [key: string]: number;
}

const EnhancedEditInvoiceForm: React.FC<EnhancedEditInvoiceFormProps> = ({ invoiceId }) => {
  const navigate = useNavigate();
  const [selectedLineItems, setSelectedLineItems] = useState<string[]>([]);
  const [lineItemPercentages, setLineItemPercentages] = useState<LineItemPercentage>({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Fetch existing invoice data
  const { data: invoice, isLoading: isLoadingInvoice } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          customers(id, name, last_name),
          estimates(*)
        `)
        .eq('id', invoiceId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!invoiceId,
  });

  // Fetch existing invoice line items to determine selected items
  const { data: invoiceLineItems } = useQuery({
    queryKey: ['invoiceLineItems', invoiceId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoice_line_items')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!invoiceId,
  });

  const estimateId = invoice?.estimate_id || null;
  const isStandalone = !estimateId;
  const { estimate, lineItems, existingInvoices, isLoadingEstimate } = useInvoiceFormData(estimateId);
  const { estimateTotal, totalInvoiced, remainingBalance } = useInvoiceCalculations(
    isStandalone ? [] : (lineItems || []),
    isStandalone ? [] : (existingInvoices || []),
    isStandalone ? null : (estimate?.adjustment_metadata as any)
  );
  const updateInvoiceMutation = useInvoiceUpdate();

  // Calculate remaining balance excluding current invoice for display
  const currentInvoiceAmount = invoice?.total_amount || 0;
  const remainingBalanceForEdit = remainingBalance + currentInvoiceAmount;

  const form = useForm<InvoiceFormValues>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: {
      title: '',
      description: '',
      payment_terms: '',
      special_instructions: '',
      invoice_type: 'full',
      selected_line_items: [],
    },
  });

  const invoiceType = form.watch('invoice_type');
  const percentageOfEstimate = form.watch('percentage_of_estimate');
  const customAmount = form.watch('custom_amount');

  // Calculate invoice amount in real-time
  const calculateInvoiceAmount = () => {
    switch (invoiceType) {
      case 'full':
        return remainingBalanceForEdit;
      case 'percentage':
        return percentageOfEstimate ? (estimateTotal * percentageOfEstimate) / 100 : 0;
      case 'custom_amount':
        return customAmount || 0;
      case 'partial':
        if (!lineItems || selectedLineItems.length === 0) return 0;
        const selectedItems = lineItems.filter(item => selectedLineItems.includes(item.id));
        return selectedItems.reduce((sum, item) => {
          const percentage = lineItemPercentages[item.id] || 100;
          const itemCost = item.manual_override && item.manual_total_cost !== null
            ? item.manual_total_cost
            : item.total_cost_snapshot;
          return sum + (itemCost * percentage / 100);
        }, 0);
      default:
        return 0;
    }
  };

  const calculatedInvoiceAmount = calculateInvoiceAmount();
  const remainingAfterInvoice = remainingBalanceForEdit - calculatedInvoiceAmount;

  // Check editability and redirect if not allowed
  const editability = invoice ? checkInvoiceEditability(invoice) : null;

  useEffect(() => {
    if (invoice && editability && !editability.canEdit) {
      toast.error(editability.reason || 'This invoice cannot be edited');
      navigate(`/invoices/${invoiceId}`);
    }
  }, [invoice, editability, invoiceId, navigate]);

  // Initialize form with existing invoice data
  useEffect(() => {
    if (invoice && !isInitialized && editability?.canEdit) {
      form.reset({
        title: invoice.title,
        description: invoice.description || '',
        due_date: invoice.due_date ? new Date(invoice.due_date) : undefined,
        payment_terms: invoice.payment_terms || '',
        special_instructions: invoice.special_instructions || '',
        invoice_type: isStandalone ? 'full' : ((invoice.invoice_type as any) || 'full'),
        percentage_of_estimate: invoice.percentage_of_estimate || undefined,
        custom_amount: invoice.invoice_type === 'custom_amount' ? invoice.total_amount : undefined,
        selected_line_items: [],
      });

      // Set selected line items from existing invoice line items
      if (invoiceLineItems && invoiceLineItems.length > 0) {
        const selectedIds = invoiceLineItems.map(item => item.estimate_line_item_id);
        setSelectedLineItems(selectedIds);
        
        // Initialize percentages to 100 for selected items
        const percentages: LineItemPercentage = {};
        selectedIds.forEach(id => {
          percentages[id] = 100;
        });
        setLineItemPercentages(percentages);
      }

      setIsInitialized(true);
    }
  }, [invoice, invoiceLineItems, form, isInitialized, editability]);

  const onSubmit = async (values: InvoiceFormValues) => {
    if (!invoice) {
      console.error('Missing invoice data');
      return;
    }

    // For standalone invoices, skip estimate-dependent update
    if (isStandalone) {
      // Simple update for standalone invoices
      const { error } = await supabase
        .from('invoices')
        .update({
          title: values.title,
          description: values.description,
          due_date: values.due_date
            ? `${values.due_date.getFullYear()}-${String(values.due_date.getMonth() + 1).padStart(2, '0')}-${String(values.due_date.getDate()).padStart(2, '0')}`
            : null,
          payment_terms: values.payment_terms,
          special_instructions: values.special_instructions,
        })
        .eq('id', invoiceId);

      if (error) {
        toast.error('Failed to update invoice');
        return;
      }
      toast.success('Invoice updated successfully');
      navigate(`/invoices/${invoiceId}`);
      return;
    }

    if (!estimate || !lineItems) {
      console.error('Missing estimate or line items');
      return;
    }

    updateInvoiceMutation.mutate({
      invoiceId,
      values: { 
        ...values, 
        selected_line_items: selectedLineItems 
      },
      estimate,
      lineItems,
      selectedLineItems,
      lineItemPercentages,
      existingInvoices: existingInvoices || [],
      estimateTotal,
    });
  };

  const handleLineItemSelection = (itemId: string, checked: boolean) => {
    if (checked) {
      const newSelectedItems = [...selectedLineItems, itemId];
      setSelectedLineItems(newSelectedItems);
      setLineItemPercentages(prev => ({ ...prev, [itemId]: 100 }));
      form.setValue('selected_line_items', newSelectedItems);
    } else {
      const newSelectedItems = selectedLineItems.filter(id => id !== itemId);
      setSelectedLineItems(newSelectedItems);
      setLineItemPercentages(prev => {
        const updated = { ...prev };
        delete updated[itemId];
        return updated;
      });
      form.setValue('selected_line_items', newSelectedItems);
    }
  };

  const handleLineItemPercentageChange = (itemId: string, percentage: number) => {
    setLineItemPercentages(prev => ({ ...prev, [itemId]: percentage }));
  };

  if (isLoadingInvoice || !invoice) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="p-6 text-center text-muted-foreground">
          Loading invoice data...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {editability?.showWarning && (
        <Alert variant="destructive" className="bg-amber-50 border-amber-200 text-amber-800">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800">Warning</AlertTitle>
          <AlertDescription className="text-amber-700">
            {editability.warningMessage}
          </AlertDescription>
        </Alert>
      )}
      
      {!isStandalone && (
        <InvoiceProgressSummary
          estimateTotal={estimateTotal}
          totalInvoiced={totalInvoiced - currentInvoiceAmount}
          remainingBalance={remainingBalanceForEdit}
        />
      )}

      {isStandalone ? (
        invoice.status === 'draft' ? (
          <StandaloneInvoiceForm 
            isEditMode 
            invoiceId={invoiceId} 
            initialData={invoice} 
          />
        ) : (
          <Card>
            <CardContent className="pt-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <InvoiceFormFields form={form} />
                  <div className="flex flex-col-reverse sm:flex-row gap-2">
                    <Button type="button" variant="outline" asChild className="w-full sm:w-auto">
                      <Link to={`/invoices/${invoiceId}`}>Cancel</Link>
                    </Button>
                    <Button type="submit" disabled={updateInvoiceMutation.isPending} className="w-full sm:w-auto">
                      Update Invoice
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        )
      ) : (
        <InvoiceFormWrapper
          estimateId={estimateId}
          isLoadingEstimate={isLoadingEstimate}
          estimate={estimate}
        >
          <InvoiceFormContent
            form={form}
            invoiceType={invoiceType}
            remainingBalance={remainingBalanceForEdit}
            lineItems={lineItems}
            selectedLineItems={selectedLineItems}
            lineItemPercentages={lineItemPercentages}
            currentInvoiceAmount={calculatedInvoiceAmount}
            remainingAfterInvoice={remainingAfterInvoice}
            estimateId={estimateId!}
            isCreating={updateInvoiceMutation.isPending}
            onSubmit={onSubmit}
            onLineItemSelection={handleLineItemSelection}
            onLineItemPercentageChange={handleLineItemPercentageChange}
            isEditMode={true}
            invoiceId={invoiceId}
          />
        </InvoiceFormWrapper>
      )}
    </div>
  );
};

export default EnhancedEditInvoiceForm;
